/**
 * KonexGuide — guía mascota anclada en el flujo `/agendar` (Konex Fase A, guionada).
 *
 * - Avatar circular anclado (esquina inferior-izquierda desktop · pill superior mobile)
 *   + burbuja de mensaje. Ancla FIJA en los 6 pasos; cambia expresión, acento y
 *   animación según el `tone` del mensaje.
 * - Cero IA, cero datos del usuario. El "cerebro" es `konexBrain` (función pura).
 * - Persistencia en `localStorage` vía `useKonexState` (no hay cuentas de paciente).
 *
 * Spec: docs/spec_konex_guide_booking.md · Roadmap: docs/KONEX_APP_ROADMAP.md §3.4.
 * Montado en `BookingWizardContent`, hermano de `.booking-content`, dentro de
 * `<BookingProvider>` (para que Fase B pueda leer `useBooking().state` sin remontar).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import {
  motion,
  useReducedMotion,
  type TargetAndTransition,
  type Transition,
  type Variants,
} from 'framer-motion';
import type { BookingStep } from '../BookingWizard';
import {
  konexBrain,
  getScriptedMessage,
  type KonexScriptedMessage,
  type KonexTone,
} from './konexBrain';
import { useKonexState } from './useKonexState';
import './KonexGuide.css';

type KonexDataState = 'open' | 'collapsed' | 'dismissed';

interface KonexGuideProps {
  currentStep: BookingStep;
  isExisting: boolean;
  /**
   * Fase B (§6.3) — `undefined`/`null` = todavía no se resolvió el estado del
   * nudge de nombre_pareja (RPC en curso); `true`/`false` = valor conocido.
   * Mientras esté sin resolver Y el paso sea 'existing-patient', el effect de
   * despacho de mensaje espera — evita la carrera entre el timer de Konex
   * (DEFER_MS) y el RPC: sin esto, Konex podría marcar 'existing-patient' como
   * visto con el saludo genérico antes de saber que correspondía el nudge, y
   * el nudge no se mostraría nunca (el gate de dedupe es el mismo `seenSteps`).
   */
  parejaNudge?: boolean | null;
}

const TONE_ASSET: Record<KonexTone, string> = {
  contenido: '/assets/img/konex/konex-contenido.webp',
  calido: '/assets/img/konex/konex-calido.webp',
  jugueton: '/assets/img/konex/konex-jugueton.webp',
};

/**
 * Asset de cabeza recortada para los renders chicos (§7): avatar colapsado /
 * descartado (56px desktop) y pill mobile (32px). El asset de tono muestra
 * cabeza+torso+brazos — a 32px se pierde la cara; este es solo cabeza+corona.
 * El asset de tono se reserva para el avatar 72px de desktop con la burbuja abierta.
 */
const AVATAR_COLLAPSED_ASSET = '/assets/img/konex/konex-avatar-collapsed.webp';

const MOBILE_QUERY = '(max-width: 768px)';
/** §5.1 — Konex entra DESPUÉS de que se asienta la transición del wizard (300ms). */
const DEFER_MS = 360;
/** §6.2 — margen para no tapar los botones Anterior/Siguiente apilados en mobile. */
const NEAR_BOTTOM_PX = 200;

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

function computeIsMobile(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches;
}

/**
 * El header sticky del wizard (56px) solo existe en los 3 pasos con navegación;
 * verification / existing-patient / success corren en rut-capture-mode sin header
 * (BookingWizard.tsx: showNavigation). Determina el anclaje mobile de Konex y si
 * el auto-hide near-bottom aplica (los botones Anterior/Siguiente que despeja solo
 * viven en esos 3 pasos).
 */
function stepHasWizardHeader(step: BookingStep): boolean {
  return (
    step === 'service-therapist' || step === 'calendar' || step === 'patient-confirmation'
  );
}

const KonexGuide: FC<KonexGuideProps> = ({ currentStep, isExisting, parejaNudge }) => {
  const { seenSteps, dismissed, markSeen, dismiss } = useKonexState();
  const reduce = useReducedMotion() === true;

  const hasWizardHeader = stepHasWizardHeader(currentStep);

  // Al montar, reconstruye el último mensaje visto desde localStorage para que el
  // avatar colapsado / descartado persista entre recargas (spec §2 · §10.4). El
  // effect diferido maneja aparte el mensaje NUEVO del paso actual si lo hay.
  const [activeMessage, setActiveMessage] = useState<KonexScriptedMessage | null>(() =>
    seenSteps.length > 0 ? getScriptedMessage(seenSteps[seenSteps.length - 1]) : null,
  );
  const [dataState, setDataState] = useState<KonexDataState>(() =>
    dismissed ? 'dismissed' : 'collapsed',
  );
  const [hoverPeek, setHoverPeek] = useState(false);
  const [failedAssets, setFailedAssets] = useState<string[]>([]);

  const [isMobile, setIsMobile] = useState<boolean>(computeIsMobile);
  const [nearBottom, setNearBottom] = useState(false);

  const avatarRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Snapshot del último valor para el effect diferido (deps = [currentStep], §5.1).
  const latestRef = useRef({ seenSteps, dismissed, isExisting, isMobile });
  latestRef.current = { seenSteps, dismissed, isExisting, isMobile };

  const isOpen = dataState === 'open';

  /* ---- isMobile con listener (rotación / resize) ---- */
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  /* ---- nearBottom (solo mobile), scroll throttleado con requestAnimationFrame ----
     Solo aplica en los 3 pasos con navegación: el auto-hide despeja los botones
     Anterior/Siguiente apilados, que no existen en verification/existing-patient/
     success. Y solo si la página REALMENTE scrollea — sin la guarda `scrollable`,
     una página corta (innerHeight ≈ scrollHeight) da nearBottom=true de entrada y
     esconde la pill de Konex para siempre en mobile. */
  useEffect(() => {
    if (!isMobile || !hasWizardHeader) {
      setNearBottom(false);
      return;
    }
    let raf = 0;
    const measure = () => {
      raf = 0;
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollable = scrollHeight > window.innerHeight + 1;
      setNearBottom(
        scrollable && window.innerHeight + window.scrollY >= scrollHeight - NEAR_BOTTOM_PX,
      );
    };
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(measure);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    measure();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [isMobile, hasWizardHeader]);

  /* ---- Mensaje por paso — DIFERIDO (gotcha §5.1 / patrón FUNC-075) ----
     La transición AnimatePresence del wizard (300ms) + el window.scrollTo smooth
     de BookingWizardContent descartan cualquier animación encolada en el MISMO
     tick que el cambio de currentStep. Un setTimeout(360ms) corre muchos ticks
     después — la transición del wizard ya terminó, la animación de Konex entra
     limpia. NO se anida requestAnimationFrame: no dispara con la pestaña en
     segundo plano (rAF pausado) y dejaría a Konex sin montar hasta el refoco. */
  useEffect(() => {
    // §6.3 — para 'existing-patient' esperamos a que se resuelva el nudge de
    // pareja antes de decidir qué decir (ver comentario de la prop más arriba).
    if (currentStep === 'existing-patient' && parejaNudge === undefined) return;
    if (currentStep === 'existing-patient' && parejaNudge === null) return;

    const snap = latestRef.current;
    const msg = konexBrain({
      step: currentStep,
      isExisting: snap.isExisting,
      seenSteps: snap.seenSteps,
      parejaNudge: parejaNudge === true,
    });

    if (!msg) {
      // Paso ya visto o sin guion: nada nuevo. Si había burbuja abierta, colapsar
      // para no dejar a la vista un mensaje de otro paso.
      setDataState((prev) => (prev === 'open' ? 'collapsed' : prev));
      return;
    }

    const timer = window.setTimeout(() => {
      setActiveMessage(msg);
      markSeen(currentStep);
      // Desktop auto-abre; mobile queda en pill; dismissed nunca auto-abre
      // (el paso igual queda marcado como visto — §2 fila "descartado").
      if (!latestRef.current.dismissed) {
        setDataState(latestRef.current.isMobile ? 'collapsed' : 'open');
      }
    }, DEFER_MS);

    return () => {
      // Avance rápido entre pasos → no mostrar la burbuja de un paso viejo.
      window.clearTimeout(timer);
    };
  }, [currentStep, markSeen, parejaNudge]);

  /* ---- Body scroll-lock + foco inicial: tarjeta ~modal en mobile ---- */
  useEffect(() => {
    if (!isMobile || !isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const raf = window.requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      document.body.style.overflow = previousOverflow;
      window.cancelAnimationFrame(raf);
    };
  }, [isMobile, isOpen]);

  /* ---- Esc + trampa de foco mientras data-state="open" ----
     Desktop: Esc con el foco dentro de la burbuja → colapsa, devuelve foco al avatar
     (la burbuja NO es modal, no atrapa Tab).
     Mobile: tarjeta ~modal → Esc siempre cierra + Tab atrapado dentro de la burbuja. */
  useEffect(() => {
    if (!isOpen) return;

    const collapseToAvatar = () => {
      setDataState('collapsed');
      avatarRef.current?.focus();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const bubble = bubbleRef.current;
        const focusInside = !!bubble && bubble.contains(document.activeElement);
        if (isMobile || focusInside) {
          e.stopPropagation();
          collapseToAvatar();
        }
        return;
      }

      if (e.key !== 'Tab' || !isMobile) return;
      const bubble = bubbleRef.current;
      if (!bubble) return;
      const focusables = getFocusable(bubble);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !bubble.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !bubble.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, isMobile]);

  /* ---- Handlers ---- */
  const handleAvatarClick = useCallback(() => {
    setHoverPeek(false);
    setDataState((prev) => (prev === 'open' ? 'collapsed' : 'open'));
  }, []);

  const handleClose = useCallback(() => {
    dismiss();
    setDataState('dismissed');
    avatarRef.current?.focus();
  }, [dismiss]);

  const handleScrim = useCallback(() => {
    setDataState('collapsed');
    avatarRef.current?.focus();
  }, []);

  const handleImgError = useCallback((src: string) => {
    setFailedAssets((prev) => (prev.includes(src) ? prev : [...prev, src]));
  }, []);

  /* ---- Variantes Framer Motion (§5) ---- */
  const bubbleVariants = useMemo<Variants>(() => {
    const HIDDEN_VIS = { visibility: 'hidden' } as const;
    // §5.3 — salida uniforme (micro), en los 3 tonos.
    const exitTransition: Transition = { duration: 0.2, ease: [0.4, 0, 0.2, 1] };

    if (isMobile) {
      // §5.4 — tarjeta ~modal: animación estructural, no tonal.
      const mobileReduced: Variants = {
        hidden: { opacity: 0, transition: { duration: 0.2 }, transitionEnd: HIDDEN_VIS },
        open: { opacity: 1, visibility: 'visible', transition: { duration: 0.2 } },
      };
      const mobileFull: Variants = {
        hidden: { opacity: 0, y: -8, transition: exitTransition, transitionEnd: HIDDEN_VIS },
        open: {
          opacity: 1,
          y: 0,
          visibility: 'visible',
          transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
        },
      };
      return reduce ? mobileReduced : mobileFull;
    }

    if (reduce) {
      const desktopReduced: Variants = {
        hidden: { opacity: 0, transition: { duration: 0.2 }, transitionEnd: HIDDEN_VIS },
        contenido: { opacity: 1, visibility: 'visible', transition: { duration: 0.2 } },
        calido: { opacity: 1, visibility: 'visible', transition: { duration: 0.2 } },
        jugueton: { opacity: 1, visibility: 'visible', transition: { duration: 0.2 } },
      };
      return desktopReduced;
    }

    // §3 / §5.2 — intensidad de entrada por tono.
    const desktopFull: Variants = {
      hidden: { opacity: 0, y: 4, transition: exitTransition, transitionEnd: HIDDEN_VIS },
      contenido: {
        opacity: 1,
        y: [6, 0],
        visibility: 'visible',
        transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
      },
      calido: {
        opacity: 1,
        y: [10, 0],
        scale: [0.98, 1],
        visibility: 'visible',
        transition: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
      },
      jugueton: {
        opacity: 1,
        y: [14, -2, 0],
        scale: [0.96, 1, 1],
        visibility: 'visible',
        transition: { duration: 0.42, ease: [0.34, 1.56, 0.64, 1], times: [0, 0.7, 1] },
      },
    };
    return desktopFull;
  }, [isMobile, reduce]);

  // §5.2 — 1 bob del avatar en tono juguetón (no loop); nada bajo reduced-motion.
  // Memoizado para no re-disparar el bob en re-renders no relacionados (hover, scroll).
  const shouldBob = !reduce && !isMobile && activeMessage?.tone === 'jugueton' && isOpen;
  const avatarAnimate = useMemo<TargetAndTransition>(
    () => (shouldBob ? { rotate: [0, -6, 4, 0] } : { rotate: 0 }),
    [shouldBob],
  );

  if (!activeMessage) return null;

  const tone = activeMessage.tone;
  // Cabeza recortada para los renders chicos; asset de tono solo en el avatar
  // 72px de desktop con la burbuja abierta (§7).
  const assetSrc = !isMobile && isOpen ? TONE_ASSET[tone] : AVATAR_COLLAPSED_ASSET;
  const assetFailed = failedAssets.includes(assetSrc);

  const peeking = hoverPeek && !isMobile && dataState === 'collapsed';
  const bubbleShown = isOpen || peeking;
  const animateTo = bubbleShown ? (isMobile ? 'open' : tone) : 'hidden';

  const avatarLabel = isOpen ? 'Konex — ocultar mensaje' : 'Konex tiene un mensaje nuevo';

  return (
    <aside
      className={`konex-guide konex-guide--tone-${tone}`}
      data-surface={currentStep}
      data-noheader={String(!hasWizardHeader)}
      data-state={dataState}
      data-has-unseen={String(dataState !== 'open')}
      data-nearbottom={String(nearBottom)}
      aria-label="Konex, tu guía en la reserva"
    >
      {/* Scrim: solo mobile + data-state="open" (todo lo maneja el CSS). Cierra al tap. */}
      <div className="konex-guide__scrim" aria-hidden="true" onClick={handleScrim} />

      <motion.div
        ref={bubbleRef}
        className="konex-guide__bubble"
        id="konex-guide-bubble"
        role="group"
        aria-label="Mensaje de Konex"
        variants={bubbleVariants}
        initial="hidden"
        animate={animateTo}
      >
        <button
          ref={closeRef}
          className="konex-guide__close"
          type="button"
          aria-label="Cerrar mensaje de Konex"
          onClick={handleClose}
        >
          <svg
            className="konex-guide__close-icon"
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 20 20"
          >
            <path
              d="M5 5l10 10M15 5L5 15"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <p className="konex-guide__eyebrow">
          <span className="konex-guide__tone-dot" aria-hidden="true" />
          Konex
        </p>

        {/* Región viva: el <p> no se desmonta; solo cambia su nodo de texto (§1). */}
        <p className="konex-guide__text" aria-live="polite">
          {activeMessage.text}
        </p>
      </motion.div>

      <button
        ref={avatarRef}
        className="konex-guide__avatar"
        type="button"
        tabIndex={isMobile && isOpen ? -1 : 0}
        aria-expanded={isOpen}
        aria-controls="konex-guide-bubble"
        aria-label={avatarLabel}
        onClick={handleAvatarClick}
        onMouseEnter={() => setHoverPeek(true)}
        onMouseLeave={() => setHoverPeek(false)}
      >
        {assetFailed ? (
          <span className="konex-guide__avatar-placeholder" aria-hidden="true">
            K
          </span>
        ) : (
          <motion.img
            className="konex-guide__avatar-img"
            src={assetSrc}
            alt=""
            width={72}
            height={72}
            loading="lazy"
            decoding="async"
            onError={() => handleImgError(assetSrc)}
            animate={avatarAnimate}
            transition={{ duration: 0.5, delay: 0.1, ease: 'easeInOut' }}
          />
        )}
        {/* Piezas solo-mobile (pill): línea truncada + chevron. Decorativas. */}
        <span className="konex-guide__avatar-line" aria-hidden="true">
          {activeMessage.text}
        </span>
        <span className="konex-guide__chevron" aria-hidden="true" />
        {/* Punto "mensaje nuevo": el texto alterno va en el aria-label del avatar (WCAG 1.4.1). */}
        <span className="konex-guide__badge" aria-hidden="true" />
      </button>
    </aside>
  );
};

export default KonexGuide;
